/**
 * Patient Search Modal Component
 *
 * 患者検索モーダル - Allows searching and selecting patients
 * Used in PatientEditPage to populate patient data from existing records
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { PatientFormData } from '@pages/main/patient/types/patientTypes';
import { useAuth } from '@shared/providers/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useInfinitePatientSearch } from '@/hooks/main';
import { AppButton, AppModal, AppSpin, AppTable } from '@/components/atoms';
import {
  getPatientSearchColumnSortOrder,
  getPatientSearchTableInstanceKey,
  PATIENT_SEARCH_TABLE_PANEL_CLASS,
  resolvePatientSearchSort,
} from './patientSearchSort';

// ==================== Types ====================

interface PatientSearchItem {
  patient_id: number;
  serial_number: string;
  name: string;
  name_kana: string;
  address1: string;
  address2: string;
  address3: string;
  address_name: string;
  insurance_symbol: string;
  insurance_number: string;
  official: boolean | null;
}

interface PatientSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (patient: PatientFormData) => void;
  searchKeyword: string;
  setSearchKeyword: (value: string) => void;
  includeExcluded: boolean;
  setIncludeExcluded?: (value: boolean) => void;
  /** Tenant for navigation */
  tenant: string | undefined;
}
const TABLE_BODY_SCROLL_Y = 'calc(50vh - 64px)';

const convertToPatientSearchItem = (data: PatientFormData): PatientSearchItem => {
  const insuranceCard = data.health_insurance_cards?.[0];
  return {
    patient_id: data.patient?.patient_id || data.id || 0,
    serial_number: data.patient?.serial_number || data.serial_number?.toString() || '',
    name: data.patient_information?.name || data.current_patient_information?.name || '',
    name_kana:
      data.patient_information?.name_kana || data.current_patient_information?.name_kana || '',
    address1:
      data.patient_information?.address1 || data.current_patient_information?.address1 || '',
    address2:
      data.patient_information?.address2 || data.current_patient_information?.address2 || '',
    address3:
      data.patient_information?.address3 || data.current_patient_information?.address3 || '',
    address_name:
      data.patient_information?.address_name ||
      data.current_patient_information?.address_name ||
      '',
    insurance_symbol: insuranceCard?.card_mark || '',
    insurance_number: insuranceCard?.card_number || '',
    official: data.patient?.official ?? data.official ?? null,
  };
};

// ==================== Component ====================

const PatientSearchModal: React.FC<PatientSearchModalProps> = ({
  isOpen,
  onClose,
  searchKeyword,
  setSearchKeyword,
  includeExcluded,
  tenant,
}) => {
  const { clinic } = useAuth();

  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sortField, setSortField] = useState('name_kana');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';

  const filters = useMemo(
    () =>
      isOpen
        ? {
            keyword: searchKeyword,
            includeExcluded,
            sortField,
            sortOrder,
          }
        : null,
    [isOpen, searchKeyword, includeExcluded, sortField, sortOrder]
  );

  const {
    data: infiniteData,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useInfinitePatientSearch(siteType, clinicNumber, filters, { enabled: isOpen });

  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const infiniteScrollGuardRef = useRef({ hasNextPage, isFetchingNextPage, isPending });
  infiniteScrollGuardRef.current = { hasNextPage, isFetchingNextPage, isPending };

  const patientFormData = useMemo(() => {
    if (!infiniteData?.pages?.length) return [];
    return infiniteData.pages.flatMap(page => page.data);
  }, [infiniteData]);

  const patients = useMemo(
    () => patientFormData.map(convertToPatientSearchItem),
    [patientFormData]
  );
  const tableInstanceKey = getPatientSearchTableInstanceKey(sortField, sortOrder);

  const totalCount = useMemo(() => {
    if (!infiniteData?.pages?.length) return 0;
    const lastPage = infiniteData.pages[infiniteData.pages.length - 1];
    return lastPage.pagination?.total ?? patients.length;
  }, [infiniteData, patients.length]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedRowKeys([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedRowKeys([]);
    const tableBody = tableWrapperRef.current?.querySelector<HTMLElement>('.ant-table-body');
    if (tableBody) {
      tableBody.scrollTop = 0;
    }
  }, [isOpen, searchKeyword, includeExcluded, sortField, sortOrder]);

  const fetchNextPageWhenNearBottom = useCallback((scrollElement: HTMLElement) => {
    const distanceFromBottom =
      scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
    if (distanceFromBottom > 120) return;

    const {
      hasNextPage: next,
      isFetchingNextPage: fetching,
      isPending: pending,
    } = infiniteScrollGuardRef.current;
    if (next && !fetching && !pending) {
      fetchNextPageRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const tableBody = tableWrapperRef.current?.querySelector<HTMLElement>('.ant-table-body');
    if (!tableBody) return;

    const handleScroll = () => fetchNextPageWhenNearBottom(tableBody);
    tableBody.addEventListener('scroll', handleScroll);
    return () => tableBody.removeEventListener('scroll', handleScroll);
  }, [isOpen, fetchNextPageWhenNearBottom, tableInstanceKey, infiniteData?.pages?.length]);

  useEffect(() => {
    if (!isOpen || isPending || !hasNextPage || isFetchingNextPage) return;

    const tableBody = tableWrapperRef.current?.querySelector<HTMLElement>('.ant-table-body');
    if (tableBody && tableBody.scrollHeight <= tableBody.clientHeight + 1) {
      fetchNextPageRef.current();
    }
  }, [isOpen, isPending, hasNextPage, isFetchingNextPage, patients.length]);

  // HIT-14783: every action that leaves this list loads a patient (or a blank form) into the
  // screen, so the header search box is cleared as well. Close first so the cleared keyword
  // does not trigger another search while the list is still open.
  const leaveTo = (path: string) => {
    navigate(path);
    onClose();
    setSearchKeyword('');
  };

  const handleNewPatient = () => {
    leaveTo(`/${tenant}/patients/edit`);
  };

  const handleShowPatient = () => {
    if (selectedRowKeys.length === 0) {
      return;
    }
    const selectedPatient = patients.find(p => p.patient_id === selectedRowKeys[0]);
    if (selectedPatient) {
      leaveTo(`/${tenant}/patients/edit/${selectedPatient.patient_id}`);
    }
  };

  const handleCopyPatient = () => {
    if (selectedRowKeys.length === 0) {
      return;
    }
    const selectedPatient = patients.find(p => p.patient_id === selectedRowKeys[0]);
    if (selectedPatient) {
      leaveTo(`/${tenant}/patients/copy/${selectedPatient.patient_id}`);
    }
  };

  const cellTruncateClass = 'min-w-0 line-clamp-2 break-words';

  const columns = [
    {
      title: '受診者番号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 100,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('serial_number', { sortField, sortOrder }),
      render: (val: string) => <div className={cellTruncateClass}>{val ?? ''}</div>,
    },
    {
      title: '受診者名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('name', { sortField, sortOrder }),
      render: (val: string) => <div className={cellTruncateClass}>{val ?? ''}</div>,
    },
    {
      title: '受診者名カナ',
      dataIndex: 'name_kana',
      key: 'name_kana',
      width: 120,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('name_kana', { sortField, sortOrder }),
      render: (val: string) => <div className={cellTruncateClass}>{val ?? ''}</div>,
    },
    {
      title: '住所',
      dataIndex: 'address1',
      key: 'address1',
      width: 150,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('address1', { sortField, sortOrder }),
      render: (_val: string, record: PatientSearchItem) => {
        const fullAddress = [record.address1, record.address2, record.address3, record.address_name]
          .filter(Boolean)
          .join(' ');
        return <div className={cellTruncateClass}>{fullAddress || ''}</div>;
      },
    },
    {
      title: '記号',
      dataIndex: 'insurance_symbol',
      key: 'insurance_symbol',
      width: 80,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('insurance_symbol', { sortField, sortOrder }),
      render: (val: string) => <div className={cellTruncateClass}>{val ?? ''}</div>,
    },
    {
      title: '番号',
      dataIndex: 'insurance_number',
      key: 'insurance_number',
      width: 120,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('insurance_number', { sortField, sortOrder }),
      render: (val: string) => <div className={cellTruncateClass}>{val ?? ''}</div>,
    },
    {
      title: '状態',
      dataIndex: 'official',
      key: 'official',
      width: 80,
      sorter: true,
      sortOrder: getPatientSearchColumnSortOrder('official', { sortField, sortOrder }),
      align: 'left' as const,
      render: (official: boolean | null) => {
        const text = official === null ? '' : official ? '本登録' : '仮登録';
        return <div className={cellTruncateClass}>{text}</div>;
      },
    },
  ];

  if (!isOpen) return null;

  const showInitialLoading = isPending && patients.length === 0;
  const showEmpty = !isPending && !isFetchingNextPage && patients.length === 0 && !isError;

  return (
    <AppModal isOpen={isOpen} onClose={onClose} footer={null} maxWidth="max-w-5xl" title="患者検索">
      <div className="flex flex-col h-full">
        <div className="sticky top-0 z-10 flex gap-2 pb-4 bg-white">
          <AppButton onClick={handleNewPatient}>新規</AppButton>
          <AppButton onClick={handleShowPatient}>表示</AppButton>
          <AppButton onClick={handleCopyPatient}>複写</AppButton>
        </div>

        <div className={PATIENT_SEARCH_TABLE_PANEL_CLASS}>
          <div className="relative flex flex-col mb-0 rounded">
            {isError ? (
              <div className="flex items-center justify-center h-full text-center text-red-600">
                検索に失敗しました。再度お試しください。
              </div>
            ) : showEmpty ? (
              <div className="flex items-center justify-center h-full text-center text-gray-500">
                患者が見つかりません
              </div>
            ) : (
              <>
                <AppSpin spinning={showInitialLoading} size="large">
                  <AppTable
                    key={tableInstanceKey}
                    ref={tableWrapperRef}
                    className="h-full"
                    columns={columns}
                    dataSource={patients.map(p => ({
                      ...p,
                      key: p.patient_id,
                    }))}
                    pagination={false}
                    scroll={{ x: true, y: TABLE_BODY_SCROLL_Y }}
                    rowSelection={{
                      type: 'radio',
                      selectedRowKeys,
                      onChange: keys => setSelectedRowKeys(keys),
                      columnWidth: 60,
                    }}
                    onChange={(_pagination, _filters, sorter) => {
                      const sort = Array.isArray(sorter) ? sorter[0] : sorter;
                      const nextSort = resolvePatientSearchSort(sort, { sortField, sortOrder });
                      setSortField(nextSort.sortField);
                      setSortOrder(nextSort.sortOrder);
                    }}
                    onRow={record => ({
                      onClick: event => {
                        const target = event.target as HTMLElement;
                        const isSelectionCell = target.closest('td.ant-table-selection-column');
                        if (isSelectionCell) {
                          setSelectedRowKeys([record.patient_id]);
                          return;
                        }
                        leaveTo(`/${tenant}/patients/edit/${record.patient_id}`);
                      },
                      style: { cursor: 'pointer', height: 50 },
                    })}
                  />
                </AppSpin>

                {patients.length > 0 && totalCount > 0 && (
                  <div className="px-2 text-sm text-gray-600">{totalCount}件</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default PatientSearchModal;
